import { buildAuthHeader, buildRequestUrl } from "../../setup/setupAxios";
import { API_URL } from "../../constants";

interface MobileService {
    startLocationService: (routeId: string, accessToken: string) => Promise<boolean>;
    startLocationServiceIfPermitted: (routeId: string, accessToken: string) => void;
    stopLocationService: () => void;
    onOrdersRefreshed: () => void;
    performLogout: () => void;
    shouldPlayVideoFullscreen: () => boolean;
}

// Старые Android WebView (API < 29) не отображают inline-видео — проигрываем на весь экран.
const FIRST_INLINE_VIDEO_API_LEVEL = 29;

const buildGeoPositionRequestDataJson = (routeId: string, accessToken: string) => {
    const url = buildRequestUrl(`${API_URL.routes.get}/${routeId}/geoPositions`);
    return JSON.stringify({ url, token: buildAuthHeader(accessToken) });
};

// Если пользователь оставил диалог разрешения открытым и не вернулся к нему,
// не блокируем UI бесконечно.
const PERMISSION_GRANT_TIMEOUT_MS = 60_000;

const startLocationService = (routeId: string, accessToken: string): Promise<boolean> => {
    // Не-Android окружение (обычный браузер для отладки): моста нет, нечего ждать.
    if (!window.android?.startLocationService) return Promise.resolve(true);

    const dataJson = buildGeoPositionRequestDataJson(routeId, accessToken);

    return new Promise<boolean>((resolve) => {
        let settled = false;
        const finish = (ok: boolean) => {
            if (settled) return;
            settled = true;
            window.onAndroidLocationServiceStarted = undefined;
            clearTimeout(timer);
            resolve(ok);
        };

        // Нативный слой вызывает этот колбэк после старта foreground-сервиса —
        // как синхронно, так и после асинхронного предоставления разрешения.
        window.onAndroidLocationServiceStarted = () => finish(true);

        const timer = setTimeout(() => finish(false), PERMISSION_GRANT_TIMEOUT_MS);

        const startedSync = window.android.startLocationService(dataJson);
        if (startedSync) finish(true);
    });
};

// Без запроса разрешений: используется, когда нужно подхватить трекинг после того,
// как пользователь выдал разрешение через настройки уже после отказа в диалоге.
const startLocationServiceIfPermitted = (routeId: string, accessToken: string) => {
    if (!window.android?.startLocationServiceIfPermitted) return;
    window.android.startLocationServiceIfPermitted(buildGeoPositionRequestDataJson(routeId, accessToken));
};

const stopLocationService = () => {
    window.android?.stopLocationService();
};

const onOrdersRefreshed = () => {
    window.android?.onOrdersRefreshed?.();
};

const performLogout = () => {
    window.android?.performLogout?.();
};

const shouldPlayVideoFullscreen = (): boolean => {
    const apiLevel = window.android?.getApiLevel?.();
    return typeof apiLevel === "number" && apiLevel < FIRST_INLINE_VIDEO_API_LEVEL;
};

const androidService: MobileService = {
    startLocationService,
    startLocationServiceIfPermitted,
    stopLocationService,
    onOrdersRefreshed,
    performLogout,
    shouldPlayVideoFullscreen,
};

export default androidService;
